import { useEffect, useRef, useState } from 'react';
import api from '../services/ApiService';
import liveEvents from '../services/LiveEvents';
import Dialer from './Dialer';
import HistoryPanel from './HistoryPanel';
import ContactsPanel from './ContactsPanel';
import RecordingsPanel from './RecordingsPanel';
import QueueAIManagement from './QueueAIManagement';
import QueueAIPanel from './QueueAIPanel';
import MyStatePage from './MyStatePage';
import ReportsPage from './ReportsPage';
import CrmPage from './CrmPage';
import '../styles/admin.css';
import AnimatedLogo from './AnimatedLogo';
import { useBranding } from '../context/BrandingContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(s) {
  const m = Math.floor((s || 0) / 60).toString().padStart(2, '0');
  return `${m}:${((s || 0) % 60).toString().padStart(2, '0')}`;
}

function StatusDot({ color }) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8,
      borderRadius: '50%', background: color,
      marginRight: 6, flexShrink: 0,
    }} />
  );
}

const STATE_COLOR = {
  ready:   { bg: '#dcfce7', text: '#166534' },
  busy:    { bg: '#fee2e2', text: '#991b1b' },
  break:   { bg: '#fef3c7', text: '#92400e' },
  wrapup:  { bg: '#ede9fe', text: '#5b21b6' },
  offline: { bg: '#f3f4f6', text: '#6b7280' },
};

const REG_COLOR = {
  registered:        '#10b981',
  'pbx-connected':   '#10b981',
  'ws-connected':    '#f59e0b',
  'reg-failed':      '#ef4444',
  disconnected:      '#ef4444',
  'no-sip-password': '#6b7280',
  connecting:        '#6b7280',
};

const AGENT_COLOR = {
  ready:   '#10b981',
  busy:    '#ef4444',
  break:   '#f59e0b',
  wrapup:  '#8b5cf6',
  offline: '#6b7280',
};

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card">
      <div className="stat-title">{label}</div>
      <div className="stat-value" style={color ? { color } : {}}>{value ?? 0}</div>
    </div>
  );
}

// ── Spy confirm modal ─────────────────────────────────────────────────────────

function SpyModal({ agent, onConfirm, onClose }) {
  const [mode, setMode] = useState('listen');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const doSpy = async () => {
    setBusy(true);
    setError('');
    try {
      await onConfirm(agent.extension_number, mode);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const MODES = [
    { id: 'listen',  label: 'Listen',  desc: 'Silent — agent cannot hear you' },
    { id: 'whisper', label: 'Whisper', desc: 'You can speak to agent only' },
    { id: 'barge',   label: 'Barge In', desc: 'Full 3-way — all parties hear each other' },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div style={{
        background: 'white', borderRadius: 16, padding: 28, width: 380,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>
          Monitor Agent Call
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
          Agent: <strong>{agent.username}</strong>
          {agent.extension_number && ` · Ext ${agent.extension_number}`}
        </div>

        <div style={{ marginBottom: 20 }}>
          {MODES.map(m => (
            <label key={m.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '10px 12px', borderRadius: 10, marginBottom: 6, cursor: 'pointer',
              border: `2px solid ${mode === m.id ? '#2563eb' : '#e5e7eb'}`,
              background: mode === m.id ? '#eff6ff' : 'white',
            }}>
              <input
                type="radio" name="spymode" value={m.id}
                checked={mode === m.id}
                onChange={() => setMode(m.id)}
                style={{ marginTop: 2 }}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{m.label}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{m.desc}</div>
              </div>
            </label>
          ))}
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={doSpy}
            disabled={busy}
            style={{
              flex: 1, padding: '10px 0', background: '#2563eb', color: 'white',
              border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Connecting…' : `Start ${MODES.find(m => m.id === mode)?.label}`}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 18px', background: '#f3f4f6', color: '#374151',
              border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const NAV = [
  { id: 'phone',       label: 'Phone',        icon: '☎' },
  { id: 'overview',    label: 'Overview',     icon: '⊞' },
  { id: 'agents',      label: 'Agents',       icon: '👥' },
  { id: 'queues',      label: 'Queues',       icon: '☰' },
  { id: 'queue-mgmt',  label: 'Queue Members',icon: '⚙' },
  { id: 'active-calls',label: 'Active Calls', icon: '⊙' },
  { id: 'reports',     label: 'Reports',      icon: '📊' },
  { id: 'history',     label: 'Recent Calls', icon: '↺' },
  { id: 'contacts',    label: 'Contacts',     icon: '👤' },
  { id: 'recordings',  label: 'Recordings',   icon: '⏺' },
  { id: 'crm',         label: 'CRM',           icon: '🧩' },
  { id: 'ai-queue',    label: 'AI Queue',     icon: '🤖' },
  { id: 'my-state',    label: 'My State',     icon: '◉' },
];

export default function SupervisorDashboard({ user, sipPassword, sipUsername, dids = [], initialTab = '', onLogout }) {
  const branding = useBranding();
  const hasBranding = !!(branding.logo || branding.primaryColor);

  // Load tenant branding
  useEffect(() => {
    if (!branding.loaded || hasBranding || !user?.tenant_id) return;
    branding.loadBranding(user.tenant_id);
  }, [branding.loaded]); // eslint-disable-line


  const [activePage, setActivePage]   = useState(() => {
    if (initialTab === 'Dialer' || initialTab === 'phone') return 'phone';
    return 'overview';
  });

  // Supervisor data
  const [agents,      setAgents]      = useState([]);
  const [calls,       setCalls]       = useState([]);
  const [wallboard,   setWallboard]   = useState(null);
  const [queues,      setQueues]      = useState([]);
  const [queueAgents, setQueueAgents] = useState([]);
  const [expandedQueue, setExpandedQueue] = useState(null);
  const [loading,     setLoading]     = useState(true);

  // Spy modal
  const [spyTarget, setSpyTarget] = useState(null);

  // Queue membership management
  const [mgmtQueues,      setMgmtQueues]      = useState([]);
  const [mgmtSelectedId,  setMgmtSelectedId]  = useState(null);
  const [mgmtDetail,      setMgmtDetail]      = useState(null); // { queue, members, allAgents }
  const [mgmtLoading,     setMgmtLoading]     = useState(false);
  const [mgmtSaving,      setMgmtSaving]      = useState(false);
  const [mgmtError,       setMgmtError]       = useState('');
  const [mgmtAddId,       setMgmtAddId]       = useState('');   // user_id to add
  const [mgmtAddPenalty,  setMgmtAddPenalty]  = useState(0);

  // Softphone status synced from embedded Dialer
  const [sipStatus, setSipStatus] = useState({
    registration: 'connecting',
    liveConnected: false,
    agentState: 'ready',
    callState: 'idle',
    timer: 0,
    muted: false,
    onHold: false,
    isRecording: false,
    remoteNumber: '',
  });

  const [incomingCallFrom, setIncomingCallFrom] = useState(null);

  const changeAgentStateRef = useRef(null);
  const [dialTo, setDialTo] = useState('');

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadData = async () => {
    try {
      const [a, c, w, q, qa] = await Promise.all([
        api.getSupervisorAgents(),
        api.getSupervisorCalls(),
        api.getSupervisorWallboard(),
        api.getQueueStats(),
        api.getSupervisorQueueAgents(),
      ]);
      setAgents(a   || []);
      setCalls(c    || []);
      setWallboard(w || null);
      setQueues(q   || []);
      setQueueAgents(qa || []);
    } catch (e) {
      console.error('Supervisor load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const t = setInterval(loadData, 3000);

    // Live agent state push — refresh immediately when any agent state changes
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws-live`;
    let svWs = null;
    let svWsRetry = null;
    const connectSvWs = () => {
      const savedToken = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      try {
        svWs = new WebSocket(`${wsUrl}?token=${savedToken}`);
        svWs.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'agent_state' || msg.type === 'wallboard_snapshot') {
              loadData();
            }
          } catch {}
        };
        svWs.onclose = () => {
          svWsRetry = setTimeout(connectSvWs, 5000);
        };
      } catch {}
    };
    connectSvWs();

    const token = localStorage.getItem('token') || '';
    liveEvents.connect(token);

    return () => {
      clearInterval(t);
    };
  }, []);

  // Load queue list when switching to queue-mgmt page
  useEffect(() => {
    if (activePage === 'queue-mgmt') {
      loadMgmtQueues();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);

  // Load detail when selected queue changes
  useEffect(() => {
    if (mgmtSelectedId) {
      loadMgmtDetail(mgmtSelectedId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mgmtSelectedId]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const totals = wallboard?.totals || {};

  const handleDial = (number) => {
    setDialTo(number);
    setActivePage('phone');
  };

  const handleSpyConfirm = async (targetExtension, mode) => {
    await api.chanspy(targetExtension, mode);
  };

  // ── Queue membership management helpers ────────────────────────────────────

  const loadMgmtQueues = async () => {
    try {
      const qs = await api.getSupervisorQueues();
      setMgmtQueues(qs || []);
      if (!mgmtSelectedId && qs && qs.length > 0) {
        setMgmtSelectedId(qs[0].id);
      }
    } catch (e) {
      console.error('Failed to load mgmt queues', e);
    }
  };

  const loadMgmtDetail = async (queueId) => {
    if (!queueId) return;
    setMgmtLoading(true);
    setMgmtError('');
    try {
      const data = await api.getSupervisorQueueMembers(queueId);
      setMgmtDetail(data);
      setMgmtAddId('');
      setMgmtAddPenalty(0);
    } catch (e) {
      setMgmtError(e.response?.data?.error || 'Failed to load members');
    } finally {
      setMgmtLoading(false);
    }
  };

  const mgmtSelectQueue = (id) => {
    setMgmtSelectedId(id);
    loadMgmtDetail(id);
  };

  const mgmtAddMember = async () => {
    if (!mgmtAddId || !mgmtSelectedId) return;
    setMgmtSaving(true);
    setMgmtError('');
    try {
      await api.supervisorAddQueueMember(mgmtSelectedId, { user_id: Number(mgmtAddId), penalty: mgmtAddPenalty, is_active: true });
      await loadMgmtDetail(mgmtSelectedId);
    } catch (e) {
      setMgmtError(e.response?.data?.error || 'Failed to add member');
    } finally {
      setMgmtSaving(false);
    }
  };

  const mgmtRemoveMember = async (userId) => {
    if (!window.confirm('Remove this agent from the queue?')) return;
    setMgmtSaving(true);
    setMgmtError('');
    try {
      await api.supervisorRemoveQueueMember(mgmtSelectedId, userId);
      await loadMgmtDetail(mgmtSelectedId);
    } catch (e) {
      setMgmtError(e.response?.data?.error || 'Failed to remove member');
    } finally {
      setMgmtSaving(false);
    }
  };

  const mgmtToggleActive = async (userId, currentActive) => {
    setMgmtSaving(true);
    try {
      await api.supervisorUpdateQueueMember(mgmtSelectedId, userId, { is_active: !currentActive });
      await loadMgmtDetail(mgmtSelectedId);
    } catch (e) {
      setMgmtError(e.response?.data?.error || 'Failed to update member');
    } finally {
      setMgmtSaving(false);
    }
  };

  const mgmtUpdatePenalty = async (userId, penalty) => {
    try {
      await api.supervisorUpdateQueueMember(mgmtSelectedId, userId, { penalty: Number(penalty) });
      await loadMgmtDetail(mgmtSelectedId);
    } catch (e) {
      setMgmtError(e.response?.data?.error || 'Failed to update penalty');
    }
  };

  const isInCall = ['calling', 'connected', 'ringing'].includes(sipStatus.callState);
  const regColor   = REG_COLOR[sipStatus.registration] || '#6b7280';
  const agentColor = AGENT_COLOR[sipStatus.agentState]  || '#6b7280';

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="admin-shell">
      {/* ── Sidebar ── */}
      <div className="sidebar">
        <div className="sidebar-header">
          {hasBranding && branding.logo ? (
            <div style={{ background:'#fff', borderRadius:14, padding:'12px 10px 8px', boxShadow:'0 2px 12px rgba(0,0,0,.25)', marginBottom:8, display:'flex', flexDirection:'column', alignItems:'center' }}>
              <img src={branding.logo} alt={branding.companyName || ''} style={{ maxWidth:160, maxHeight:80, objectFit:'contain', display:'block' }} />
              {branding.companyName && <div style={{ fontSize:9, fontWeight:700, color:'#0D2244', letterSpacing:'1.5px', textTransform:'uppercase', marginTop:4, textAlign:'center' }}>{branding.companyName}</div>}
            </div>
          ) : (
            <>
              <AnimatedLogo width={162} style={{ display: 'block', margin: '0 auto' }} />
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <div style={{ fontWeight: 800, fontSize: '15px', color: '#f9fafb', letterSpacing: '-0.2px' }}>CallEssential</div>
                <div style={{ fontSize: '11px', color: '#c084fc', fontWeight: 500, marginTop: '1px' }}>by Synergytalk</div>
              </div>
            </>
          )}
          <div className="sidebar-subtitle" style={{ marginTop: hasBranding ? '8px' : '6px', color: hasBranding ? '#C9A84C' : undefined }}>Supervisor</div>
          <div style={{
            marginTop: '10px',
            background: hasBranding ? 'rgba(201,168,76,0.12)' : 'rgba(168,85,247,0.15)',
            color: hasBranding ? '#E2BF6B' : '#e9d5ff',
            padding: '6px 10px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            border: hasBranding ? '1px solid rgba(201,168,76,0.3)' : '1px solid rgba(168,85,247,0.28)',
          }}>
            {user.display_name || user.username}
            {sipUsername && (
              <div style={{ fontSize: '11px', color: hasBranding ? '#C9A84C' : '#c084fc', fontWeight: 500, marginTop: '2px' }}>
                Ext {sipUsername}
              </div>
            )}
            {dids && dids.length > 0 && (
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px', lineHeight: 1.4 }}>
                {dids.map(d => <div key={d}>{d}</div>)}
              </div>
            )}
          </div>
        </div>

        <div className="sidebar-menu">
          {NAV.map(item => (
            <div
              key={item.id}
              className={`sidebar-item${activePage === item.id ? ' active' : ''}`}
              onClick={() => setActivePage(item.id)}
            >
              <span className="sidebar-icon" style={{ fontSize: '16px' }}>{item.icon}</span>
              {item.label}
            </div>
          ))}
        </div>

        {/* Status footer */}
        <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
            Connection
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '12px', color: '#9ca3af' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <StatusDot color={regColor} />
              SIP: {sipStatus.registration}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <StatusDot color={sipStatus.liveConnected ? '#10b981' : '#6b7280'} />
              Live: {sipStatus.liveConnected ? 'on' : 'off'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <StatusDot color={agentColor} />
              State: {sipStatus.agentState}
            </div>
          </div>

          <div style={{ marginTop: '12px', fontSize: '10px', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>
            Live Snapshot
          </div>
          {[
            { label: 'Agents', value: totals.total_agents || 0, color: '#9ca3af' },
            { label: 'Ready',  value: totals.ready        || 0, color: '#10b981' },
            { label: 'Busy',   value: totals.busy         || 0, color: '#ef4444' },
            { label: 'Calls',  value: wallboard?.active_calls || 0, color: '#f59e0b' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#9ca3af', marginBottom: '3px' }}>
              <span>{label}</span>
              <span style={{ fontWeight: 700, color }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main ── */}
      <div className="main-area">
        {/* Topbar */}
        <div className="topbar">
          <div className="topbar-title" style={{ fontSize: '18px' }}>
            {NAV.find(n => n.id === activePage)?.label ?? 'Overview'}
          </div>
          <div className="topbar-user">
            {isInCall && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: sipStatus.callState === 'connected' ? '#fee2e2' : '#fef3c7',
                color:      sipStatus.callState === 'connected' ? '#991b1b' : '#92400e',
                padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600,
              }}>
                <span style={{ width: 8, height: 8, background: 'currentColor', borderRadius: '50%', display: 'inline-block', animation: 'ce-pulse 1.2s ease-in-out infinite' }} />
                {sipStatus.callState === 'calling'   ? `Calling ${sipStatus.remoteNumber}…` :
                 sipStatus.callState === 'connected' ? `In Call · ${fmt(sipStatus.timer)}` : 'Ringing'}
              </span>
            )}
            <button className="secondary-btn small-btn" onClick={loadData} title="Refresh">↻</button>
            <div className="user-badge">{((user.display_name || user.username) || 'S')[0].toUpperCase()}</div>
            <button className="danger-btn" onClick={onLogout}>Logout</button>
          </div>
        </div>

        {/* Incoming call banner — visible when on any tab other than phone */}
        {incomingCallFrom && activePage !== 'phone' && (
          <div onClick={() => setActivePage('phone')} style={{
            background: 'linear-gradient(135deg,#0D2244,#1A56DB)', color: '#fff',
            padding: '10px 20px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
            animation: 'ce-pulse 1.2s ease-in-out infinite',
          }}>
            <span style={{ fontSize: 18 }}>📞</span>
            <span style={{ fontWeight: 700, fontSize: 13 }}>
              Incoming call from {incomingCallFrom} — click to answer
            </span>
          </div>
        )}

        {/* Page content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          background: activePage === 'phone' ? '#eef2ff' : '#f3f4f6',
        }}>

          {/* ── Phone (always mounted, hidden when not active) ── */}
          <div style={{ display: activePage === 'phone' ? 'block' : 'none' }}>
            <Dialer
              user={user}
              sipPassword={sipPassword}
              sipUsername={sipUsername}
              dids={dids}
              onLogout={onLogout}
              embedded
              dialTo={dialTo}
              onDialUsed={() => setDialTo('')}
              changeAgentStateRef={changeAgentStateRef}
              onStatusChange={(patch) => {
                if ('incomingFrom' in patch) setIncomingCallFrom(patch.incomingFrom ?? null);
                const { incomingFrom, ...rest } = patch;
                setSipStatus(prev => ({ ...prev, ...rest }));
              }}
            />
          </div>

          {/* ── Overview ── */}
          {activePage === 'overview' && (
            <div className="page-content">
              <div className="dashboard-grid" style={{ marginBottom: '24px' }}>
                <StatCard label="Total Agents"  value={totals.total_agents} />
                <StatCard label="Ready"         value={totals.ready}   color="#16a34a" />
                <StatCard label="Busy"          value={totals.busy}    color="#dc2626" />
                <StatCard label="Break"         value={totals.break}   color="#d97706" />
                <StatCard label="Wrap-up"       value={totals.wrapup}  color="#7c3aed" />
                <StatCard label="Active Calls"  value={wallboard?.active_calls} />
              </div>
              <div className="content-card">
                <div className="admin-card-header">
                  <div className="admin-page-title" style={{ fontSize: '18px', marginBottom: 0 }}>Queues</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '14px' }}>
                  {queues.length === 0 && <p className="admin-muted">No queues configured.</p>}
                  {queues.map(q => (
                    <div key={q.id} style={{ border: '1px solid #e5e7eb', borderRadius: '14px', padding: '16px', background: '#f9fafb' }}>
                      <div style={{ fontWeight: 800, fontSize: '15px', marginBottom: '4px' }}>{q.display_name || q.queue_name}</div>
                      <div className="admin-muted" style={{ marginBottom: '12px' }}>Strategy: {q.strategy}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '6px', textAlign: 'center' }}>
                        {[
                          { label: 'Members', value: q.total_members || 0, color: '#374151' },
                          { label: 'Ready',   value: q.ready_agents  || 0, color: '#16a34a' },
                          { label: 'Busy',    value: q.busy_agents   || 0, color: '#dc2626' },
                          { label: 'Break',   value: q.break_agents  || 0, color: '#d97706' },
                          { label: 'Wrap',    value: q.wrapup_agents || 0, color: '#7c3aed' },
                        ].map(({ label, value, color }) => (
                          <div key={label}>
                            <div style={{ fontWeight: 700, fontSize: '18px', color }}>{value}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>{label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Agents with spy controls ── */}
          {activePage === 'agents' && (
            <div className="page-content">
              <div className="content-card">
                <div className="admin-card-header">
                  <div className="admin-page-title" style={{ fontSize: '18px', marginBottom: 0 }}>
                    Agents <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 400 }}>({agents.length})</span>
                  </div>
                </div>
                {loading && <p className="admin-muted">Loading…</p>}
                {!loading && agents.length === 0 && <p className="admin-muted">No agent data yet.</p>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '12px' }}>
                  {agents.map(a => {
                    const sc      = STATE_COLOR[a.state] || STATE_COLOR.offline;
                    const onCall  = a.state === 'busy' || a.state === 'talking';
                    const hasExt  = !!a.extension_number;

                    return (
                      <div key={a.id} style={{
                        border: '1px solid #e5e7eb', borderRadius: '16px',
                        padding: '16px', background: 'white',
                      }}>
                        {/* Agent info row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: onCall ? '12px' : 0 }}>
                          <div style={{
                            width: 42, height: 42, borderRadius: '50%',
                            background: sc.bg, color: sc.text,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 800, fontSize: '16px', flexShrink: 0,
                          }}>
                            {(a.username || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: '15px' }}>{a.username}</div>
                            <div className="admin-muted" style={{ fontSize: '12px' }}>
                              {a.extension_number ? `Ext ${a.extension_number}` : 'No ext'} · {a.queue_name || 'no queue'}
                            </div>
                          </div>
                          <span style={{
                            background: sc.bg, color: sc.text,
                            padding: '4px 12px', borderRadius: '999px',
                            fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
                            flexShrink: 0,
                          }}>
                            {a.state || 'offline'}
                          </span>
                        </div>

                        {/* Active call details */}
                        {onCall && (
                          <div style={{
                            background: '#fee2e2', borderRadius: '10px',
                            padding: '8px 12px', marginBottom: '10px',
                            fontSize: '13px', color: '#991b1b',
                          }}>
                            📞 {a.caller_number || '?'} → {a.callee_number || '?'}
                          </div>
                        )}

                        {/* Spy action buttons — only when agent is on a call and has an extension */}
                        {onCall && hasExt && (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {[
                              { mode: 'listen',  label: '👂 Listen',  bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
                              { mode: 'whisper', label: '🤫 Whisper', bg: '#fefce8', color: '#854d0e', border: '#fde68a' },
                              { mode: 'barge',   label: '📢 Barge',   bg: '#fff1f2', color: '#9f1239', border: '#fecdd3' },
                            ].map(({ mode, label, bg, color, border }) => (
                              <button
                                key={mode}
                                onClick={() => setSpyTarget({ ...a, defaultMode: mode })}
                                style={{
                                  flex: 1, padding: '7px 4px',
                                  background: bg, color, border: `1px solid ${border}`,
                                  borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Not on call — show direct dial button */}
                        {!onCall && hasExt && (
                          <div style={{ marginTop: '10px' }}>
                            <button
                              onClick={() => handleDial(a.extension_number)}
                              style={{
                                width: '100%', padding: '7px 0',
                                background: '#eff6ff', color: '#2563eb',
                                border: '1px solid #bfdbfe', borderRadius: '8px',
                                fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                              }}
                            >
                              ☎ Dial {a.extension_number}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Queues with agents drill-down ── */}
          {activePage === 'queues' && (
            <div className="page-content">
              <div className="content-card">
                <div className="admin-card-header">
                  <div className="admin-page-title" style={{ fontSize: '18px', marginBottom: 0 }}>Queues</div>
                </div>
                {queueAgents.length === 0 && <p className="admin-muted">No queues configured.</p>}
                {queueAgents.map(q => {
                  const isOpen = expandedQueue === q.id;
                  const memberStates = q.members || [];
                  const ready   = memberStates.filter(m => m.state === 'ready').length;
                  const busy    = memberStates.filter(m => m.state === 'busy').length;
                  const onBreak = memberStates.filter(m => m.state === 'break' || m.state === 'wrapup').length;
                  const offline = memberStates.filter(m => m.state === 'offline').length;
                  return (
                    <div key={q.id} style={{ border: '1px solid #e5e7eb', borderRadius: '14px', marginBottom: '12px', overflow: 'hidden' }}>
                      {/* Queue header — click to expand */}
                      <div
                        onClick={() => setExpandedQueue(isOpen ? null : q.id)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '14px 18px', cursor: 'pointer',
                          background: isOpen ? '#f5f3ff' : '#f9fafb',
                          borderBottom: isOpen ? '1px solid #e5e7eb' : 'none',
                          userSelect: 'none',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 800, fontSize: '15px' }}>{q.display_name || q.queue_name}</div>
                          <div className="admin-muted" style={{ fontSize: '12px', marginTop: '2px' }}>
                            {memberStates.length} members · Strategy: {q.strategy}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {[
                            { label: 'Ready',   value: ready,   color: '#16a34a', bg: '#dcfce7' },
                            { label: 'Busy',    value: busy,    color: '#dc2626', bg: '#fee2e2' },
                            { label: 'Break',   value: onBreak, color: '#d97706', bg: '#fef3c7' },
                            { label: 'Offline', value: offline, color: '#6b7280', bg: '#f3f4f6' },
                          ].map(({ label, value, color, bg }) => (
                            <div key={label} style={{ textAlign: 'center', minWidth: 44, background: bg, borderRadius: '8px', padding: '4px 8px' }}>
                              <div style={{ fontWeight: 800, fontSize: '15px', color }}>{value}</div>
                              <div style={{ fontSize: '10px', color, fontWeight: 600 }}>{label}</div>
                            </div>
                          ))}
                          <span style={{ fontSize: '18px', color: '#6b7280', marginLeft: '4px' }}>{isOpen ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Expanded agent list */}
                      {isOpen && (
                        <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {memberStates.length === 0 && <p className="admin-muted">No agents in this queue.</p>}
                          {memberStates.map(m => {
                            const sc = STATE_COLOR[m.state] || STATE_COLOR.offline;
                            const onCall = m.state === 'busy';
                            return (
                              <div key={m.id} style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '10px 12px', borderRadius: '10px',
                                background: '#fff', border: '1px solid #f3f4f6',
                              }}>
                                <div style={{
                                  width: 34, height: 34, borderRadius: '50%',
                                  background: sc.bg, color: sc.text,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontWeight: 800, fontSize: '14px', flexShrink: 0,
                                }}>
                                  {(m.username || '?')[0].toUpperCase()}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, fontSize: '14px' }}>{m.username}</div>
                                  <div className="admin-muted" style={{ fontSize: '11px' }}>
                                    {m.extension_number ? `Ext ${m.extension_number}` : 'No ext'}
                                  </div>
                                </div>
                                <span style={{
                                  background: sc.bg, color: sc.text,
                                  padding: '3px 10px', borderRadius: '999px',
                                  fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                                }}>
                                  {m.state || 'offline'}
                                </span>
                                {!onCall && m.extension_number && (
                                  <button
                                    onClick={() => handleDial(m.extension_number)}
                                    style={{
                                      padding: '5px 10px', background: '#eff6ff',
                                      color: '#2563eb', border: '1px solid #bfdbfe',
                                      borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                                    }}
                                  >
                                    ☎ Dial
                                  </button>
                                )}
                                {onCall && m.extension_number && (
                                  <button
                                    onClick={() => setSpyTarget(m)}
                                    style={{
                                      padding: '5px 10px', background: '#fef2f2',
                                      color: '#dc2626', border: '1px solid #fecaca',
                                      borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                                    }}
                                  >
                                    👂 Monitor
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Queue Membership Management ── */}
          {activePage === 'queue-mgmt' && (
            <div className="page-content" style={{ display: 'flex', gap: 0, padding: 0, flex: 1, minHeight: 0 }}>

              {/* Queue list sidebar */}
              <div style={{
                width: 260, flexShrink: 0,
                borderRight: '1px solid #e5e7eb',
                background: '#f9fafb',
                display: 'flex', flexDirection: 'column',
                overflowY: 'auto',
                padding: '16px 0',
              }}>
                <div style={{ padding: '0 16px 12px', fontWeight: 800, fontSize: 13, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Queues
                </div>
                {mgmtQueues.length === 0 && (
                  <p className="admin-muted" style={{ padding: '0 16px' }}>No queues found.</p>
                )}
                {mgmtQueues.map(q => (
                  <div
                    key={q.id}
                    onClick={() => mgmtSelectQueue(q.id)}
                    style={{
                      padding: '11px 16px', cursor: 'pointer',
                      background: mgmtSelectedId === q.id ? '#ede9fe' : 'transparent',
                      borderLeft: mgmtSelectedId === q.id ? '3px solid #7c3aed' : '3px solid transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13, color: mgmtSelectedId === q.id ? '#5b21b6' : '#1f2937' }}>
                      {q.display_name || q.queue_name}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      {q.strategy} · {q.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Detail panel */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
                {!mgmtSelectedId && (
                  <p className="admin-muted">Select a queue on the left to manage its members.</p>
                )}

                {mgmtError && (
                  <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                    {mgmtError}
                  </div>
                )}

                {mgmtSelectedId && mgmtDetail && (() => {
                  const { queue, members, allAgents } = mgmtDetail;
                  const memberIds = new Set(members.map(m => m.id));
                  const nonMembers = (allAgents || []).filter(a => !memberIds.has(a.id));

                  return (
                    <>
                      {/* Queue header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 20, color: '#1f2937' }}>
                            {queue.display_name || queue.queue_name}
                          </div>
                          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                            Strategy: <strong>{queue.strategy}</strong> · {members.length} member{members.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <button
                          onClick={() => loadMgmtDetail(mgmtSelectedId)}
                          style={{ padding: '7px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                        >
                          ↻ Refresh
                        </button>
                      </div>

                      {/* Add Agent section */}
                      <div style={{
                        background: '#f5f3ff', border: '1px solid #ddd6fe',
                        borderRadius: 14, padding: '16px 18px', marginBottom: 20,
                      }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#5b21b6', marginBottom: 10 }}>
                          ＋ Add Agent to Queue
                        </div>
                        {nonMembers.length === 0 ? (
                          <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>All CC-enabled agents are already members of this queue.</p>
                        ) : (
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div style={{ flex: '1 1 200px' }}>
                              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Agent</label>
                              <select
                                value={mgmtAddId}
                                onChange={e => setMgmtAddId(e.target.value)}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd6fe', fontSize: 13 }}
                              >
                                <option value="">— select agent —</option>
                                {nonMembers.map(a => (
                                  <option key={a.id} value={a.id}>
                                    {a.username}{a.extension_number ? ` (Ext ${a.extension_number})` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={{ flex: '0 0 110px' }}>
                              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Priority (0=high)</label>
                              <input
                                type="number" min="0" max="10"
                                value={mgmtAddPenalty}
                                onChange={e => setMgmtAddPenalty(e.target.value)}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd6fe', fontSize: 13 }}
                              />
                            </div>
                            <button
                              onClick={mgmtAddMember}
                              disabled={!mgmtAddId || mgmtSaving}
                              style={{
                                padding: '9px 20px', background: 'linear-gradient(135deg,#7c3aed,#5b21b6)',
                                color: 'white', border: 'none', borderRadius: 8,
                                fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                opacity: (!mgmtAddId || mgmtSaving) ? 0.6 : 1,
                              }}
                            >
                              {mgmtSaving ? 'Adding…' : 'Add Agent'}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Members table */}
                      {mgmtLoading ? (
                        <p className="admin-muted">Loading…</p>
                      ) : members.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                          <div style={{ fontSize: 36, marginBottom: 12 }}>☰</div>
                          <p className="admin-muted">No agents in this queue yet.</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {members.map(m => {
                            const sc = STATE_COLOR[m.state] || STATE_COLOR.offline;
                            const isActive = !!m.member_active;
                            return (
                              <div key={m.id} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '12px 16px', borderRadius: 12,
                                background: isActive ? '#ffffff' : '#f9fafb',
                                border: `1px solid ${isActive ? '#e5e7eb' : '#f3f4f6'}`,
                                opacity: isActive ? 1 : 0.65,
                                transition: 'all 0.15s',
                              }}>
                                {/* Avatar */}
                                <div style={{
                                  width: 38, height: 38, borderRadius: '50%',
                                  background: sc.bg, color: sc.text,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontWeight: 800, fontSize: 15, flexShrink: 0,
                                }}>
                                  {(m.username || '?')[0].toUpperCase()}
                                </div>

                                {/* Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{m.username}</div>
                                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                                    {m.extension_number ? `Ext ${m.extension_number}` : 'No extension'}
                                  </div>
                                </div>

                                {/* State badge */}
                                <span style={{
                                  background: sc.bg, color: sc.text,
                                  padding: '3px 10px', borderRadius: 999,
                                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                                  flexShrink: 0,
                                }}>
                                  {m.state || 'offline'}
                                </span>

                                {/* Priority */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                  <label style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Priority</label>
                                  <input
                                    type="number" min="0" max="10"
                                    defaultValue={m.penalty || 0}
                                    onBlur={e => {
                                      if (Number(e.target.value) !== (m.penalty || 0)) {
                                        mgmtUpdatePenalty(m.id, e.target.value);
                                      }
                                    }}
                                    style={{
                                      width: 52, padding: '4px 6px', borderRadius: 6,
                                      border: '1px solid #e5e7eb', fontSize: 13,
                                      textAlign: 'center',
                                    }}
                                  />
                                </div>

                                {/* Active toggle */}
                                <button
                                  onClick={() => mgmtToggleActive(m.id, isActive)}
                                  disabled={mgmtSaving}
                                  title={isActive ? 'Pause (set inactive)' : 'Activate'}
                                  style={{
                                    padding: '5px 12px', borderRadius: 8, border: 'none',
                                    fontWeight: 700, fontSize: 12, cursor: 'pointer', flexShrink: 0,
                                    background: isActive ? '#fef3c7' : '#dcfce7',
                                    color:      isActive ? '#92400e' : '#166534',
                                  }}
                                >
                                  {isActive ? '⏸ Pause' : '▶ Activate'}
                                </button>

                                {/* Remove */}
                                <button
                                  onClick={() => mgmtRemoveMember(m.id)}
                                  disabled={mgmtSaving}
                                  title="Remove from queue"
                                  style={{
                                    padding: '5px 10px', borderRadius: 8, border: 'none',
                                    fontWeight: 700, fontSize: 12, cursor: 'pointer', flexShrink: 0,
                                    background: '#fee2e2', color: '#dc2626',
                                  }}
                                >
                                  ✕ Remove
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ── Active calls ── */}
          {activePage === 'active-calls' && (
            <div className="page-content">
              <div className="content-card">
                <div className="admin-card-header">
                  <div className="admin-page-title" style={{ fontSize: '18px', marginBottom: 0 }}>
                    Active Calls <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 400 }}>({calls.length})</span>
                  </div>
                </div>
                {calls.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '48px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>📞</div>
                    <p className="admin-muted">No active calls right now.</p>
                  </div>
                )}
                {calls.map(c => (
                  <div key={c.id} style={{
                    border: '1px solid #e5e7eb', borderRadius: '14px',
                    padding: '16px', marginBottom: '10px',
                    display: 'flex', gap: '16px', alignItems: 'center',
                  }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: '50%',
                      background: '#fee2e2', color: '#dc2626',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '18px', flexShrink: 0,
                    }}>
                      ☎
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '15px' }}>{c.caller_number} → {c.callee_number}</div>
                      <div className="admin-muted">Status: {c.status}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '16px', color: '#dc2626' }}>{fmt(c.duration)}</div>
                      <div className="admin-muted" style={{ fontSize: '11px' }}>duration</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── History / Contacts / Recordings (reuse agent components) ── */}
          {activePage === 'crm'       && <CrmPage onDial={handleDial} />}
          {activePage === 'reports'    && <ReportsPage />}
          {activePage === 'history'    && <HistoryPanel onDial={handleDial} supervisorMode />}
          {activePage === 'contacts'   && <ContactsPanel onDial={handleDial} />}
          {activePage === 'recordings' && <RecordingsPanel />}
          {activePage === 'ai-queue'   && <QueueAIManagement user={user} />}

          {/* ── My State ── */}
          {activePage === 'my-state' && (
            <MyStatePage
              agentState={sipStatus.agentState}
              queueName="default"
              onStateChange={(s) => changeAgentStateRef.current?.(s)}
            />
          )}
        </div>
      </div>

      {/* ── Spy modal ── */}
      {spyTarget && (
        <SpyModal
          agent={spyTarget}
          onConfirm={handleSpyConfirm}
          onClose={() => setSpyTarget(null)}
        />
      )}
    </div>
  );
}
